import * as React from "react";
import { NavLink, Outlet } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Snackbar, Alert } from "@mui/material";
import SecurityAlertDialog from "../../components/SecurityAlertDialog";
import NotificationBell from "../../components/NotificationBell";
import LanguageSwitcher from "../../components/LanguageSwitcher";
import { setSecurityAlertHandler } from "../../services/api";
import { useIdleTimer } from "../../hooks/useIdleTimer";
import { connectSocket, disconnectSocket } from "../../services/socket.service";
import AuthService from "../../services/auth.service";

import Box from "@mui/material/Box";
import Drawer from "@mui/material/Drawer";
import List from "@mui/material/List";
import Divider from "@mui/material/Divider";
import ListItem from "@mui/material/ListItem";
import ListItemButton from "@mui/material/ListItemButton";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";

import SupervisorAccountIcon from "@mui/icons-material/SupervisorAccount";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import SettingsIcon from "@mui/icons-material/Settings";
import ListAltIcon from "@mui/icons-material/ListAlt";
import EmojiTransportationIcon from "@mui/icons-material/EmojiTransportation";
import {
  Person as PersonIcon,
  Dashboard as DashboardIcon,
  Logout as LogoutIcon,
  DirectionsCar,
} from "@mui/icons-material";

import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Menu from "@mui/material/Menu";
import MenuIcon from "@mui/icons-material/Menu";
import Container from "@mui/material/Container";
import Avatar from "@mui/material/Avatar";
import Tooltip from "@mui/material/Tooltip";
import MenuItem from "@mui/material/MenuItem";
import BaseBreadcrumbs from "../../components/Base/Breadcrumbs";
import EnvironmentBanner from "../../components/EnvironmentBanner";
import { RoleProvider, useRole } from "../../context/RoleContext";

export const Dashboard = () => {
  const { t } = useTranslation();
  const [securityAlertOpen, setSecurityAlertOpen] = React.useState(false);
  const [idleWarnOpen, setIdleWarnOpen] = React.useState(false);

  // Register the security alert callback with the API interceptor once on mount
  React.useEffect(() => {
    setSecurityAlertHandler(() => setSecurityAlertOpen(true));
  }, []);

  // Connect socket on mount and disconnect on unmount
  React.useEffect(() => {
    connectSocket();
    return () => disconnectSocket();
  }, []);

  const handleIdleLogout = React.useCallback(() => {
    setIdleWarnOpen(false);
    AuthService.logout();
  }, []);

  const handleIdleWarn = React.useCallback(() => {
    setIdleWarnOpen(true);
  }, []);

  useIdleTimer(handleIdleWarn, handleIdleLogout);

  return (
    <RoleProvider>
      <Box className="w-full">
        <EnvironmentBanner />
        <ResponsiveAppBar />

        <Container className="py-5">
          <Outlet />
        </Container>

        <SecurityAlertDialog open={securityAlertOpen} />

        <Snackbar
          open={idleWarnOpen}
          anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
          onClose={() => setIdleWarnOpen(false)}
        >
          <Alert severity="warning" onClose={() => setIdleWarnOpen(false)}>
            {t("alerts.sessionExpiring")}
          </Alert>
        </Snackbar>
      </Box>
    </RoleProvider>
  );
};

export function ResponsiveAppBar() {
  const { t } = useTranslation();
  const [anchorElUser, setAnchorElUser] = React.useState<null | HTMLElement>(
    null
  );
  const [adminData, setAdminData] = React.useState<any>(null);

  React.useEffect(() => {
    const storedAdmin = localStorage.getItem("adminData");
    if (storedAdmin) {
      try {
        setAdminData(JSON.parse(storedAdmin));
      } catch (error) {
        console.error("Failed to parse admin data:", error);
      }
    }
  }, []);

  const handleOpenUserMenu = (event: React.MouseEvent<HTMLElement>) => {
    setAnchorElUser(event.currentTarget);
  };

  const handleCloseUserMenu = () => {
    setAnchorElUser(null);
  };

  const handleLogout = () => {
    handleCloseUserMenu();
    AuthService.logout();
  };

  const handleProfile = () => {
    window.location.href = "/dashboard/profile";
    handleCloseUserMenu();
  };

  const settings = [
    {
      label: t("nav.profile"),
      icon: <PersonIcon />,
      onClick: handleProfile,
    },
    {
      label: t("nav.logout"),
      icon: <LogoutIcon />,
      onClick: handleLogout,
    },
  ];

  const adminInitial = adminData?.firstName?.[0]?.toUpperCase() || "A";

  return (
    <AppBar position="static" className="w-fit">
      <Box className="w-full bg-slate-600 px-2">
        <Toolbar disableGutters>
          <Box className="flex-grow gap-4 flex items-center">
            <DashboardDrawer />
            <BaseBreadcrumbs />
          </Box>

          <Box sx={{ flexGrow: 0, display: "flex", alignItems: "center" }}>
            <LanguageSwitcher />
            <NotificationBell />
            <Tooltip title={t("nav.openSettings")}>
              <IconButton onClick={handleOpenUserMenu} sx={{ p: 0 }}>
                <Avatar
                  sx={{
                    bgcolor: "#1976d2",
                    width: 40,
                    height: 40,
                    fontWeight: "bold",
                  }}
                >
                  {adminInitial}
                </Avatar>
              </IconButton>
            </Tooltip>
            <Menu
              sx={{ mt: "45px" }}
              id="menu-appbar"
              anchorEl={anchorElUser}
              anchorOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              keepMounted
              transformOrigin={{
                vertical: "top",
                horizontal: "right",
              }}
              open={Boolean(anchorElUser)}
              onClose={handleCloseUserMenu}
            >
              {settings.map((setting) => (
                <MenuItem key={setting.label} onClick={setting.onClick}>
                  <Typography sx={{ textAlign: "center" }}>
                    {setting.label}
                  </Typography>
                </MenuItem>
              ))}
            </Menu>
          </Box>
        </Toolbar>
      </Box>
    </AppBar>
  );
}

export function DashboardDrawer() {
  const [open, setOpen] = React.useState(false);
  const { isRelationshipManager } = useRole();
  const { t } = useTranslation();

  const toggleDrawer = (newOpen: boolean) => () => {
    setOpen(newOpen);
  };

  const DrawerList = (
    <Box sx={{ width: 250 }} role="presentation" onClick={toggleDrawer(false)}>
      <List>
        <ListItem disablePadding>
          <ListItemButton component={NavLink} to="/dashboard">
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText primary={t("nav.home")} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={NavLink} to="/dashboard/manage-owners">
            <ListItemIcon>
              <SupervisorAccountIcon />
            </ListItemIcon>
            <ListItemText primary={t("nav.owners")} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={NavLink} to="/dashboard/manage-car-washes">
            <ListItemIcon>
              <EmojiTransportationIcon />
            </ListItemIcon>
            <ListItemText
              primary={isRelationshipManager ? t("nav.washingCenters") : t("nav.carWashes")}
            />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton component={NavLink} to="/dashboard/washes">
            <ListItemIcon>
              <EmojiTransportationIcon />
            </ListItemIcon>
            <ListItemText primary={t("nav.washManagement")} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={NavLink}
            to="/dashboard/vehicle-catalog"
            disabled={isRelationshipManager}
            sx={isRelationshipManager ? { opacity: 0.4, pointerEvents: "none" } : undefined}
          >
            <ListItemIcon>
              <DirectionsCar />
            </ListItemIcon>
            <ListItemText primary={t("nav.vehicleCatalog")} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={NavLink}
            to="/dashboard/vehicle-type-reviews"
            disabled={isRelationshipManager}
            sx={isRelationshipManager ? { opacity: 0.4, pointerEvents: "none" } : undefined}
          >
            <ListItemIcon>
              <DirectionsCar />
            </ListItemIcon>
            <ListItemText primary={t("nav.vehicleTypeReviews")} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={NavLink}
            to="/dashboard/manage-users"
            disabled={isRelationshipManager}
            sx={isRelationshipManager ? { opacity: 0.4, pointerEvents: "none" } : undefined}
          >
            <ListItemIcon>
              <PersonIcon />
            </ListItemIcon>
            <ListItemText primary={t("nav.users")} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={NavLink}
            to="/dashboard/admins"
            disabled={isRelationshipManager}
            sx={isRelationshipManager ? { opacity: 0.4, pointerEvents: "none" } : undefined}
          >
            <ListItemIcon>
              <AdminPanelSettingsIcon />
            </ListItemIcon>
            <ListItemText primary={t("nav.adminManagement")} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={NavLink}
            to="/dashboard/settings/washing-class"
            disabled={isRelationshipManager}
            sx={isRelationshipManager ? { opacity: 0.4, pointerEvents: "none" } : undefined}
          >
            <ListItemIcon>
              <SettingsIcon />
            </ListItemIcon>
            <ListItemText primary={t("nav.washingClassSettings")} />
          </ListItemButton>
        </ListItem>
        <ListItem disablePadding>
          <ListItemButton
            component={NavLink}
            to="/dashboard/settings/reasons"
            disabled={isRelationshipManager}
            sx={isRelationshipManager ? { opacity: 0.4, pointerEvents: "none" } : undefined}
          >
            <ListItemIcon>
              <ListAltIcon />
            </ListItemIcon>
            <ListItemText primary={t("nav.reasons")} />
          </ListItemButton>
        </ListItem>
      </List>
      <Divider />
    </Box>
  );

  return (
    <div>
      <IconButton
        size="large"
        aria-label={t("nav.accountOfCurrentUser")}
        aria-controls="menu-appbar"
        aria-haspopup="true"
        onClick={toggleDrawer(true)}
        color="inherit"
      >
        <MenuIcon />
      </IconButton>
      <Drawer open={open} onClose={toggleDrawer(false)}>
        {DrawerList}
      </Drawer>
    </div>
  );
}
